/**
 * i18n configuration for the application
 * This file sets up internationalization using svelte-i18n
 */

import { browser } from "$app/environment";
import { init, register, locale, _, isLoading } from "svelte-i18n";
import { writable, get } from "svelte/store";

// Define supported locales
const SUPPORTED_LOCALES = ["en", "de"];
const DEFAULT_LOCALE = "de";

export const languageStore = writable<string>(DEFAULT_LOCALE);

function getLocaleFromBrowser(): string {
  if (!browser) return DEFAULT_LOCALE;

  const savedLocale = localStorage.getItem("language");
  if (savedLocale && SUPPORTED_LOCALES.includes(savedLocale)) {
    return savedLocale;
  }

  try {
    const browserLocale = navigator.language.split("-")[0];
    return SUPPORTED_LOCALES.includes(browserLocale)
      ? browserLocale
      : DEFAULT_LOCALE;
  } catch (e) {
    return DEFAULT_LOCALE;
  }
}

register("en", () => import("./locales/en.json"));
register("de", () => import("./locales/de.json"));

let coreI18nInitialized = false;
const coreI18nInitializationPromise = (async () => {
  if (coreI18nInitialized) return;

  const initialLoadLocale = browser ? getLocaleFromBrowser() : DEFAULT_LOCALE;

  console.log(
    `[i18n Core] Attempting to initialize svelte-i18n with locale: ${initialLoadLocale}`
  );
  await init({
    fallbackLocale: DEFAULT_LOCALE,
    initialLocale: initialLoadLocale
  });
  console.log(
    `[i18n Core] svelte-i18n init() called for locale: ${initialLoadLocale}`
  );

  if (get(isLoading)) {
    console.log("[i18n Core] Waiting for initial locale to load...");
    await new Promise<void>((resolve, reject) => {
      const unsubscribe = isLoading.subscribe((loading) => {
        if (!loading) {
          console.log("[i18n Core] Initial locale loaded.");
          unsubscribe();
          resolve();
        }
      });
      setTimeout(() => {
        if (get(isLoading)) {
          console.error(
            "[i18n Core] Timeout waiting for initial locale to load."
          );
          unsubscribe();
          reject(new Error("Timeout waiting for initial locale to load."));
        }
      }, 10000);
    });
  }
  coreI18nInitialized = true;
  console.log("[i18n Core] svelte-i18n core initialization sequence complete.");
})();

coreI18nInitializationPromise.catch((error) => {
  console.error(
    "[i18n Core] CRITICAL ERROR in coreI18nInitializationPromise:",
    error
  );
});

export async function initClientI18n(): Promise<void> {
  if (!browser) {
    return;
  }

  await coreI18nInitializationPromise;

  const clientLocale = getLocaleFromBrowser();
  languageStore.set(clientLocale);

  languageStore.subscribe(async (newLocaleValue) => {
    if (localStorage.getItem("language") !== newLocaleValue) {
      localStorage.setItem("language", newLocaleValue);
    }
    if (get(locale) !== newLocaleValue) {
      try {
        await locale.set(newLocaleValue);
      } catch (e) {
        console.error(
          "[i18n Client] Error setting locale from languageStore subscription:",
          e
        );
      }
    }
  });

  if (get(locale) !== clientLocale) {
    try {
      await locale.set(clientLocale);
    } catch (e) {
      console.error(
        "[i18n Client] Error aligning svelte-i18n locale in initClientI18n:",
        e
      );
    }
  }
}

export { locale, _, SUPPORTED_LOCALES, DEFAULT_LOCALE, isLoading };
